import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import 'moment-timezone';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Button } from '../../../../web/components-v2/Button';
import { Calendar } from '../../../../web/components-v2/Calendar';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { CheckIcon } from '../../../../web/components/svgs/icons/CheckIcon';
import {
	prefetchSchedulerAvailabilityQuery,
	useSchedulerAvailabilitySuspenseQuery,
} from '../../../queries/useSchedulerAvailabilityQuery';
import { s } from './styles';

interface Props {
	toolCall: Api.MeetingSchedulingToolCall;
	onTimeSlotSelected?: (formattedTimeText: string) => void;
}

interface TimeSlot {
	start: string;
	end?: string;
	label: string;
}

export function MeetingSchedulingWidgetContent({ toolCall, onTimeSlotSelected }: Props) {
	const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
	const meetingId = toolCall.meetingId;
	const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
	const [selectedSlot, setSelectedSlot] = useState<string | undefined>(undefined);
	const [monthOffset, setMonthOffset] = useState(0);
	const [displayedMonth, setDisplayedMonth] = useState<Date>(() => new Date());
	const [confirmed, setConfirmed] = useState(false);

	const { data: availabilities } = useSchedulerAvailabilitySuspenseQuery({
		meetingId,
		monthOffset,
	});
	// prefetch the next month's availability
	useEffect(() => {
		prefetchSchedulerAvailabilityQuery({
			meetingId,
			monthOffset: monthOffset + 1,
		});
	}, [monthOffset, meetingId]);

	/** Compute the first day of the month that the availability array covers */
	const referenceDate = useMemo(() => {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
	}, [monthOffset]);

	/** Build the set of disabled days (no availability) for the displayed month */
	const disabledDays = useMemo(() => {
		if (!availabilities) return [];

		const disabled: Date[] = [];
		const daysInMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0).getDate();

		for (let i = 0; i < daysInMonth; i++) {
			const daySlots = availabilities[i];
			if (!daySlots || daySlots.length === 0) {
				disabled.push(new Date(referenceDate.getFullYear(), referenceDate.getMonth(), i + 1));
			}
		}

		return disabled;
	}, [availabilities, referenceDate]);

	/** Get time slots for the selected day */
	const slotsForSelectedDay: TimeSlot[] = useMemo(() => {
		if (!selectedDate || !availabilities) return [];

		const dayIndex = selectedDate.getDate() - 1;
		const daySlots = availabilities[dayIndex];
		if (!daySlots || daySlots.length === 0) return [];

		return daySlots
			.filter((slot: Api.ISchedulerDailyIntervals): slot is Api.ISchedulerDailyIntervals => Boolean(slot?.start))
			.map((slot: Api.ISchedulerDailyIntervals) => ({
				start: slot.start!,
				end: slot.end,
				label: moment.tz(slot.start, timezone).format('h:mm A'),
			}));
	}, [selectedDate, availabilities, timezone]);

	const handleMonthChange = (month: Date) => {
		setDisplayedMonth(month);
		const now = new Date();
		const baseMonth = now.getMonth();
		const baseYear = now.getFullYear();
		const newOffset = (month.getFullYear() - baseYear) * 12 + (month.getMonth() - baseMonth);
		setMonthOffset(Math.max(0, newOffset));
		setSelectedDate(undefined);
		setSelectedSlot(undefined);
	};

	const handleDaySelect = (date: Date | undefined) => {
		setSelectedDate(date);
		setSelectedSlot(undefined);
	};

	const handleSlotClick = (slotStart: string) => {
		setSelectedSlot(prev => (prev === slotStart ? undefined : slotStart));
	};

	const handleConfirm = () => {
		if (!selectedSlot || !selectedDate) return;

		const m = moment.tz(selectedSlot, timezone);
		const formatted = m.format('dddd, MMMM D, YYYY [at] h:mm A');
		onTimeSlotSelected?.(`I'd like to book ${formatted}`);
		setConfirmed(true);
	};

	if (confirmed && selectedSlot) {
		const m = moment.tz(selectedSlot, timezone);
		return (
			<div className={css(s.container)}>
				<MeetingHeader toolCall={toolCall} />
				<div className={css(s.confirmedContainer)}>
					<CheckIcon className={css(s.checkmark)} />
					<span>{m.format('dddd, MMMM D [at] h:mm A')}</span>
				</div>
			</div>
		);
	}

	return (
		<div className={css(s.container)}>
			<MeetingHeader toolCall={toolCall} />

			<div className={css(s.calendarSection)}>
				<Calendar
					mode='single'
					styles={[s.compactCalendar]}
					selected={selectedDate}
					onSelect={handleDaySelect}
					month={displayedMonth}
					onMonthChange={handleMonthChange}
					disabled={[{ before: new Date() }, ...disabledDays]}
					showOutsideDays={false}
				/>
			</div>

			{selectedDate ? (
				<div className={css(s.slotsSection)}>
					<div className={css(s.slotsLabel)}>{moment(selectedDate).format('ddd, MMM D')}</div>
					{slotsForSelectedDay.length === 0 ? (
						<div className={css(s.noSlots)}>No available times for this date.</div>
					) : (
						<div className={css(s.slotsGrid)}>
							{slotsForSelectedDay.map((slot: TimeSlot) => (
								<button
									key={slot.start}
									type='button'
									className={css(s.slotButton, selectedSlot === slot.start && s.slotButtonSelected)}
									onClick={() => handleSlotClick(slot.start)}
								>
									{slot.label}
								</button>
							))}
						</div>
					)}
				</div>
			) : null}

			{selectedSlot ? (
				<div className={css(s.confirmRow)}>
					<Button variant='primary' size='sm' styles={[s.confirmButton]} onClick={handleConfirm}>
						Book
					</Button>
				</div>
			) : null}
		</div>
	);
}

/* const LOCATION_TYPE_LABELS: Record<string, string> = {
	Phone: 'Phone call',
	InPerson: 'In person',
	Virtual: 'Virtual',
	AllowInviteeChoice: "Invitee's choice",
	Other: 'Other',
};

function getLocationTypeLabel(locationType: string | undefined): string | undefined {
	if (!locationType) return undefined;
	// Hide "Unknown" (backend fallback) and pass through anything unmapped as-is.
	if (locationType === 'Unknown') return undefined;
	return LOCATION_TYPE_LABELS[locationType] ?? locationType;
} */

function MeetingHeader({ toolCall }: { toolCall: Api.MeetingSchedulingToolCall }) {
	//const locationLabel = getLocationTypeLabel(toolCall.locationType);
	return (
		<div className={css(s.header)}>
			<h4 className={css(s.meetingName)}>{toolCall.meetingName}</h4>
			<div className={css(s.meetingMeta)}>
				<span>{toolCall.duration}</span>
				{/* Not showing location type for now */}
				{/* {locationLabel ? (
					<>
						<span className={css(s.metaSeparator)} />
						<span>{locationLabel}</span>
					</>
				) : null} */}
			</div>
		</div>
	);
}

export const MeetingSchedulingWidget = ({ toolCall, onTimeSlotSelected }: Props) => {
	return (
		<ErrorBoundary
			fallback={
				<div className={css(s.container)}>
					<MeetingHeader toolCall={toolCall} />
					<div className={css(s.errorMessage)}>Scheduling session no longer available.</div>
				</div>
			}
		>
			<Suspense
				fallback={
					<div className={css(s.container)}>
						<MeetingHeader toolCall={toolCall} />
						<div className={css(s.calendarSection)}>
							<div className={css(s.loadingContainer)}>
								<LoadingSpinner type='tiny' />
							</div>
						</div>
					</div>
				}
			>
				<MeetingSchedulingWidgetContent toolCall={toolCall} onTimeSlotSelected={onTimeSlotSelected} />
			</Suspense>
		</ErrorBoundary>
	);
};
